import React, { useState, useEffect } from 'react';
import { Vendor, VendorCreate, VendorUpdate } from '../types/costwand.d';
import { Input } from './ui/input.tsx';
import { Button } from './ui/button.tsx';
import { Label } from '@radix-ui/react-label'; // Using Radix Label for accessibility
import { Spinner } from './ui/spinner.tsx'; // Import Spinner

interface VendorFormProps {
  initialData?: Vendor | null;
  onSubmit: (data: VendorCreate | VendorUpdate) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const defaultFormData: VendorCreate = {
  name: '',
  address: '',
  city: '',
  phone: 0,
  email: '',
  code: 0,
  created_by: 'tester', // Default value for created_by
};

const VendorForm: React.FC<VendorFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<VendorCreate | VendorUpdate>(
    initialData
      ? {
          name: initialData.name,
          address: initialData.address,
          city: initialData.city,
          phone: initialData.phone,
          email: initialData.email,
          code: initialData.code,
          updated_by: initialData.updated_by || 'tester', // Use existing or default
        }
      : defaultFormData
  );

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        address: initialData.address,
        city: initialData.city,
        phone: initialData.phone,
        email: initialData.email,
        code: initialData.code,
        updated_by: initialData.updated_by || 'tester',
      });
    } else {
      setFormData(defaultFormData);
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === 'number' ? parseInt(value, 10) || 0 : value, // Parse code, phone to integer
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name" className="block text-sm font-medium text-foreground mb-1">Name</Label>
        <Input
          id="name"
          type="text"
          required
          value={formData.name || ''}
          onChange={handleChange}
          disabled={isSubmitting}
        />
      </div>
      <div>
        <Label htmlFor="address" className="block text-sm font-medium text-foreground mb-1">Address</Label>
        <Input
          id="address"
          type="text"
          required
          value={formData.address || ''}
          onChange={handleChange}
          disabled={isSubmitting}
        />
      </div>
      <div>
        <Label htmlFor="city" className="block text-sm font-medium text-foreground mb-1">City</Label>
        <Input
          id="city"
          type="text"
          required
          value={formData.city || ''}
          onChange={handleChange}
          disabled={isSubmitting}
        />
      </div>
      <div>
        <Label htmlFor="phone" className="block text-sm font-medium text-foreground mb-1">Phone</Label>
        <Input
          id="phone"
          type="number"
          required
          value={formData.phone || ''}
          onChange={handleChange}
          disabled={isSubmitting}
        />
      </div>
      <div>
        <Label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">Email</Label>
        <Input
          id="email"
          type="email"
          required
          value={formData.email || ''}
          onChange={handleChange}
          disabled={isSubmitting}
        />
      </div>
      <div>
        <Label htmlFor="code" className="block text-sm font-medium text-foreground mb-1">Code</Label>
        <Input
          id="code"
          type="number"
          required
          value={formData.code || ''}
          onChange={handleChange}
          disabled={isSubmitting}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <span className="flex items-center gap-2"><Spinner size="sm" /> Saving...</span> : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default VendorForm;